import { createApi } from "@reduxjs/toolkit/query/react";

import apiConfig from "configs/apiConfig";
import { REDUCER_PATHS } from "store/config";
import { transformResponseHandler } from "utils/transformResponseHandler";

import { customFetchBase } from ".";

const masterApi = createApi({
  reducerPath: REDUCER_PATHS.MASTER,
  baseQuery: customFetchBase,
  endpoints: (builder) => ({
    getTimeZone: builder.query({
      query: () => ({
        url: apiConfig.TIMEZONE,
        method: "GET",
      }),
      providesTags: ["Timezone"],
    }),

    timezoneList: builder.mutation({
      query: (data) => ({
        url: apiConfig.TIMEZONE_LIST,
        method: "POST",
        body: data,
      }),
    }),
    timezoneDelete: builder.mutation({
      query: (data) => ({
        url: apiConfig.TIMEZONE,
        method: "DELETE",
        body: data,
      }),
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    addTimezone: builder.mutation({
      query: (data) => ({
        url: apiConfig.TIMEZONE,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Timezone"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    getCountry: builder.query({
      query: () => ({
        url: apiConfig.COUNTRY,
        method: "GET",
      }),
      providesTags: ["Country"],
    }),
    timezoneChangeStatus: builder.mutation({
      query: (data) => ({
        url: apiConfig.TIMEZONE,
        body: data,
        method: "PATCH",
      }),
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    // industry
    industryList: builder.query({
      query: (data) => ({
        url: `${apiConfig.INDUSTRY_LIST}?page=${data?.page}&pageSize=${data?.pageSize}&searchText=${data?.searchText}&sortColumn=${data?.sortColumn}&sortDirection=${data?.sortDirection}&languageId=${data?.languageId}`,
        method: "GET",
      }),
      providesTags: ["IndustryList"],
    }),
    addIndustryList: builder.mutation({
      query: (data) => ({
        url: apiConfig.INDUSTRY_LIST,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["IndustryList"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    editIndustryList: builder.mutation({
      query: (data) => ({
        url: `${apiConfig.INDUSTRY_LIST}/${data?.industryId}`,
        body: data?.body,
        method: "PUT",
      }),
      invalidatesTags: ["IndustryList"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    industryListDelete: builder.mutation({
      query: (data) => ({
        url: apiConfig.INDUSTRY_LIST,
        method: "DELETE",
        body: data,
      }),
      invalidatesTags: ["IndustryList"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    industryListChangeStatus: builder.mutation({
      query: (data) => ({
        url: apiConfig.INDUSTRY_LIST,
        body: data,
        method: "PATCH",
      }),
      invalidatesTags: ["IndustryList"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    industryDetails: builder.query({
      query: (data) => ({
        url: `${apiConfig.INDUSTRY_LIST}?page=1&pageSize=10&slug=${data.slug}&languageId=${data.languageId}`,
        method: "GET",
      }),
      providesTags: ["IndustryList"],
    }),
    //amenities
    amenitiesList: builder.query({
      query: (data) => ({
        url: `${apiConfig.AMENITIES_LIST}?page=${data?.page}&pageSize=${data?.pageSize}&searchText=${data.searchText}&sortColumn=${data.sortColumn}&sortDirection=${data.sortDirection}&languageId=${data.languageId}`,
        method: "GET",
      }),
      providesTags: ["AmenitiesList"],
    }),
    editAmenitiesList: builder.mutation({
      query: (data) => ({
        url: `${apiConfig.AMENITIES_LIST}/${data?.amenityId}`,
        body: data?.body,
        method: "PUT",
      }),
      invalidatesTags: ["AmenitiesList"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    amenitiesListChangeStatus: builder.mutation({
      query: (data) => ({
        url: apiConfig.AMENITIES_LIST,
        body: data,
        method: "PATCH",
      }),
      invalidatesTags: ["AmenitiesList"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    amenitiesListDelete: builder.mutation({
      query: (data) => ({
        url: apiConfig.AMENITIES_LIST,
        method: "DELETE",
        body: data,
      }),
      invalidatesTags: ["AmenitiesList"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    addAmenitiesList: builder.mutation({
      query: (data) => ({
        url: apiConfig.AMENITIES_LIST,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["AmenitiesList"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    amenityDetails: builder.query({
      query: (data) => ({
        url: `${apiConfig.AMENITIES_LIST}?page=1&pageSize=10&slug=${data?.slug}&languageId=${data?.languageId}`,
        method: "GET",
      }),
      providesTags: ["AmenitiesList"],
    }),
    // country
    countryList: builder.query({
      query: (data) => ({
        url: `${apiConfig.COUNTRY}?page=${data?.page}&pageSize=${data?.pageSize}&searchText=${data.searchText}&sortColumn=${data.sortColumn}&sortDirection=${data.sortDirection}`,
        method: "GET",
      }),
      providesTags: ["countryListing"],
    }),

    countryListDelete: builder.mutation({
      query: (data) => ({
        url: apiConfig.COUNTRY,
        method: "DELETE",
        body: data,
      }),
      invalidatesTags: ["countryListing"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    countryListChangeStatus: builder.mutation({
      query: (data) => ({
        url: apiConfig.COUNTRY,
        body: data,
        method: "PATCH",
      }),
      invalidatesTags: ["Currency", "countryListing"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    addCountryList: builder.mutation({
      query: (data) => ({
        url: apiConfig.COUNTRY,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Currency", "Country", "countryListing"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    currencyList: builder.query({
      query: () => ({
        url: apiConfig.CURRENCY_LIST,
        method: "GET",
      }),
      providesTags: ["Currency"],
    }),

    // Language
    getLanguage: builder.query({
      query: () => ({
        url: apiConfig.GET_LANGUAGE,
        method: "GET",
      }),
      providesTags: ["Language"],
    }),

    languageList: builder.query({
      query: (data) => ({
        url: `${apiConfig.LANGUAGE}?page=${data.page}&pageSize=${data.pageSize}&searchText=${data.searchText}&sortColumn=${data.sortColumn}&sortDirection=${data.sortDirection}`,
        method: "GET",
      }),
      providesTags: ["languageListing"],
    }),
    languageListChangeStatus: builder.mutation({
      query: (data) => ({
        url: apiConfig.LANGUAGE,
        body: data,
        method: "PATCH",
      }),
      invalidatesTags: ["Language", "languageListing"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    languageListDelete: builder.mutation({
      query: (data) => ({
        url: apiConfig.LANGUAGE,
        method: "DELETE",
        body: data,
      }),
      invalidatesTags: ["Language", "languageListing"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    addLanguageList: builder.mutation({
      query: (data) => ({
        url: apiConfig.LANGUAGE,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["Language", "languageListing"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),

    // Subsidiaries
    subsidiaryList: builder.query({
      query: (data) => ({
        url: `${apiConfig.SUBSIDIARY_LIST}?page=${data?.page}&pageSize=${data?.pageSize}&searchText=${data?.searchText}&sortColumn=${data?.sortColumn}&sortDirection=${data?.sortDirection}&languageId=${data?.languageId}`,
        method: "GET",
      }),
      providesTags: ["SubsidiaryList"],
    }),
    addSubsidiaryList: builder.mutation({
      query: (data) => ({
        url: apiConfig.SUBSIDIARY_LIST,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["SubsidiaryList"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    editSubsidiaryList: builder.mutation({
      query: (data) => ({
        url: `${apiConfig.SUBSIDIARY_LIST}/${data?.subsidiaryId}`,
        body: data?.body,
        method: "PUT",
      }),
      invalidatesTags: ["SubsidiaryList"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    subsidiaryListDelete: builder.mutation({
      query: (data) => ({
        url: apiConfig.SUBSIDIARY_LIST,
        method: "DELETE",
        body: data,
      }),
      invalidatesTags: ["SubsidiaryList"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    subsidiaryListChangeStatus: builder.mutation({
      query: (data) => ({
        url: apiConfig.SUBSIDIARY_LIST,
        body: data,
        method: "PATCH",
      }),
      invalidatesTags: ["SubsidiaryList"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    subsidiaryDetails: builder.query({
      query: (data) => ({
        url: `${apiConfig.SUBSIDIARY_LIST}?page=1&pageSize=10&slug=${data.slug}&languageId=${data.languageId}`,
        method: "GET",
      }),
      providesTags: ["SubsidiaryList"],
    }),
    //Gemstone Type
    gemsotneTypeList: builder.query({
      query: (data) => ({
        url: `${apiConfig.GEMSTONE_TYPE_LIST}?page=${data?.page}&pageSize=${data?.pageSize}&searchText=${data.searchText}&sortColumn=${data.sortColumn}&sortDirection=${data.sortDirection}&languageId=${data.languageId}`,
        method: "GET",
      }),
      providesTags: ["GemstoneTypeList"],
    }),
    editGemsotneTypeList: builder.mutation({
      query: (data) => ({
        url: `${apiConfig.GEMSTONE_TYPE_LIST}/${data?.gemstoneTypeId}`,
        body: data?.body,
        method: "PUT",
      }),
      invalidatesTags: ["GemstoneTypeList"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    gemsotneTypeListChangeStatus: builder.mutation({
      query: (data) => ({
        url: apiConfig.GEMSTONE_TYPE_LIST,
        body: data,
        method: "PATCH",
      }),
      invalidatesTags: ["GemstoneTypeList"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    gemsotneTypeListDelete: builder.mutation({
      query: (data) => ({
        url: apiConfig.GEMSTONE_TYPE_LIST,
        method: "DELETE",
        body: data,
      }),
      invalidatesTags: ["GemstoneTypeList"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    addGemsotneTypeList: builder.mutation({
      query: (data) => ({
        url: apiConfig.GEMSTONE_TYPE_LIST,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["GemstoneTypeList"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    gemsotneTypeDetails: builder.query({
      query: (data) => ({
        url: `${apiConfig.GEMSTONE_TYPE_LIST}?page=1&pageSize=10&slug=${data?.slug}&languageId=${data?.languageId}`,
        method: "GET",
      }),
      providesTags: ["GemstoneTypeList"],
    }),
    // Role
    roleList: builder.query({
      query: (data) => ({
        url: `${apiConfig.ROLE_LIST}?page=${data?.page}&pageSize=${data?.pageSize}&searchText=${data?.searchText}&sortColumn=${data?.sortColumn}&sortDirection=${data?.sortDirection}&languageId=${data?.languageId}`,
        method: "GET",
      }),
      providesTags: ["RoleList"],
    }),
    addRoleList: builder.mutation({
      query: (data) => ({
        url: apiConfig.ROLE_LIST,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["RoleList"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    editRoleList: builder.mutation({
      query: (data) => ({
        url: `${apiConfig.ROLE_LIST}/${data?.roleId}`,
        body: data?.body,
        method: "PUT",
      }),
      invalidatesTags: ["RoleList"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    roleListDelete: builder.mutation({
      query: (data) => ({
        url: apiConfig.ROLE_LIST,
        method: "DELETE",
        body: data,
      }),
      invalidatesTags: ["RoleList"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    roleListChangeStatus: builder.mutation({
      query: (data) => ({
        url: apiConfig.ROLE_LIST,
        body: data,
        method: "PATCH",
      }),
      invalidatesTags: ["RoleList"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    roleDetails: builder.query({
      query: (data) => ({
        url: `${apiConfig.ROLE_LIST}?page=1&pageSize=10&slug=${data.slug}&languageId=${data.languageId}`,
        method: "GET",
      }),
      providesTags: ["RoleList"],
    }),
    // Property Type
    propertyTypeList: builder.query({
      query: (data) => ({
        url: `${apiConfig.PROPERTY_TYPE_LIST}?page=${data?.page}&pageSize=${data?.pageSize}&searchText=${data?.searchText}&sortColumn=${data?.sortColumn}&sortDirection=${data?.sortDirection}&languageId=${data?.languageId}`,
        method: "GET",
      }),
      providesTags: ["PropertyTypeList"],
    }),
    editPropertyTypeList: builder.mutation({
      query: (data) => ({
        url: `${apiConfig.PROPERTY_TYPE_LIST}/${data?.propertyTypeId}`,
        body: data?.body,
        method: "PUT",
      }),
      invalidatesTags: ["PropertyTypeList"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    propertyTypeListChangeStatus: builder.mutation({
      query: (data) => ({
        url: apiConfig.PROPERTY_TYPE_LIST,
        body: data,
        method: "PATCH",
      }),
      invalidatesTags: ["PropertyTypeList"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    propertyTypeListDelete: builder.mutation({
      query: (data) => ({
        url: apiConfig.PROPERTY_TYPE_LIST,
        method: "DELETE",
        body: data,
      }),
      invalidatesTags: ["PropertyTypeList"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    addPropertyTypeList: builder.mutation({
      query: (data) => ({
        url: apiConfig.PROPERTY_TYPE_LIST,
        method: "POST",
        body: data,
      }),
      invalidatesTags: ["PropertyTypeList"],
      transformResponse: (resp) => transformResponseHandler(resp),
    }),
    propertyTypeDetails: builder.query({
      query: (data) => ({
        url: `${apiConfig.PROPERTY_TYPE_LIST}?page=1&pageSize=10&slug=${data?.slug}&languageId=${data?.languageId}`,
        method: "GET",
      }),
      providesTags: ["PropertyTypeList"],
    }),
  }),
});

export const {
  // Industry hooks
  useAddIndustryListMutation,
  useIndustryListChangeStatusMutation,
  useIndustryListDeleteMutation,
  useEditIndustryListMutation,
  useLazyIndustryListQuery,
  useLazyIndustryDetailsQuery,
  // Subsidiary hooks
  useLazySubsidiaryListQuery,
  useAddSubsidiaryListMutation,
  useEditSubsidiaryListMutation,
  useSubsidiaryListDeleteMutation,
  useSubsidiaryListChangeStatusMutation,
  useLazySubsidiaryDetailsQuery,
  useGetTimeZoneQuery,
  useTimezoneListMutation,
  useTimezoneDeleteMutation,
  useAddTimezoneMutation,
  useGetCountryQuery,
  useTimezoneChangeStatusMutation,
  useLazyCountryListQuery,
  useCountryListDeleteMutation,
  useCountryListChangeStatusMutation,
  useAddCountryListMutation,
  useCurrencyListQuery,
  useGetLanguageQuery,
  useLazyLanguageListQuery,
  useLanguageListChangeStatusMutation,
  useLanguageListDeleteMutation,
  useAddLanguageListMutation,
  useAddAmenitiesListMutation,
  useLazyAmenitiesListQuery,
  useAmenitiesListChangeStatusMutation,
  useAmenitiesListDeleteMutation,
  useLazyAmenityDetailsQuery,
  useEditAmenitiesListMutation,
  useAddGemsotneTypeListMutation,
  useEditGemsotneTypeListMutation,
  useLazyGemsotneTypeDetailsQuery,
  useGemsotneTypeListDeleteMutation,
  useLazyGemsotneTypeListQuery,
  useGemsotneTypeListChangeStatusMutation,
  // Role hooks
  useLazyRoleListQuery,
  useAddRoleListMutation,
  useEditRoleListMutation,
  useRoleListDeleteMutation,
  useRoleListChangeStatusMutation,
  useLazyRoleDetailsQuery,
  useLazyPropertyTypeListQuery,
  useAddPropertyTypeListMutation,
  useEditPropertyTypeListMutation,
  useLazyPropertyTypeDetailsQuery,
  usePropertyTypeListChangeStatusMutation,
  usePropertyTypeListDeleteMutation,
} = masterApi;

export default masterApi;
